import * as S from './styles';
import { colors } from '@/constants';
import { CartItems } from '../../hooks/CartContext';
import { useAddToCart, useCalcPrice, useComboCartPrice } from '@/talons';
import { useComboCartItems } from '@/talons/combos/useComboCartItems';
import { getLiveComboDisplayPortionQuantity } from '@/talons/combos/comboCartDisplayMerge';
import {
    resolveComboSectionTargetInstanceIds,
    setPendingComboSectionDecrement,
    setPendingComboSectionIncrement,
} from '@/talons/combos/comboCartSectionDecrement';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { computeSyncedDiscount } from '@/talons/combos/comboCartFlexSync';
import { PackingTypes, formatCurrency } from '@/shared';
import { RiInformation2Line, RiExchangeDollarLine } from 'react-icons/ri';
import { Counter, ToolTip, StPrice, Stock, SeloCampanha, SeloCombo, ProductImage } from '@/components';
import { ProductProps, PriceProductProps, PackagingProductProps, CardDescription } from '@/types';
import { useCart, useGlobals, useCartTotals, useUserContext, useDiscountFlex, useDiscountAmountFlex, useClient, useStock, useModalAttributes, useModalFlex, useTables, useSelectedClientScopeWithFallback } from '@/hooks';

const getOriginalValue = (value: number, percent: number) => {
  const porcentagemDecimal = percent / 100;
  const fatorMultiplicador = 1 + porcentagemDecimal;
  return value / fatorMultiplicador;
};

interface Product extends ProductProps {
  quantity: number;
  packing: string;
  currentPrice?: PriceProductProps;
  sku: number;
  uid: string;
  option_uid: string;
  category_id: number;
  comboPortion?: 'combo' | 'excess';
  displayQuantity?: number;
  siblingQuantity?: number;
  displayKey?: string;
  cartQuantity?: number;
  comboDiscountQuantity?: number;
  showLooseComboParticipant?: boolean;
  comboSourceUids?: string[];
  comboInstanceId?: string;
  kitIndex?: number;
  allocationId?: string;
  grupoId?: string;
  idCampanha?: string;
  mergedInstanceIds?: string[];
}
interface CardProps {
  item: Product | any;
  isFirst?: boolean;
  addProductToCart?: any;
  updateProductAmount?: any;
  isInComboGroup?: boolean;
}

const CartProductCard = ({ item, isInComboGroup = false }: CardProps) => {
  const { substTrib } = useCalcPrice();
  const { selectedClient } = useClient();
  const selectedClientScope = useSelectedClientScopeWithFallback();
  const { stockLeft: stock } = useStock();
  const { rcaSaldoFlex } = useUserContext();
  const { openModal } = useModalAttributes();
  const { fetchCorrectPrice, listaTributacao } = useCartTotals();
  const { openModal: openModalFlex } = useModalFlex();
  const { paymentCode, cardDescription } = useGlobals();
  const modePayment = useMemo(() => Number(paymentCode), [paymentCode]);
  const { removeProductFromCart, getLimit, updateProductAmount, currentCartDetails } = useCart();
  const { getComboCartItemInfo } = useComboCartItems();
  const { discountAmountAll: discountAmount } = useDiscountAmountFlex();
  const { discounts, getDiscountProduct, setDiscount, deleteDiscount } = useDiscountFlex();
  const [tooltip, setTooltip] = useState(false);
  // const [isOpenFlexDiscountModal, setOpenFlexDiscountModal] = useState(false);

  const [packType, qtd] = useMemo(() => {
    return item?.packing?.split('_');
  }, [item?.packing]);

  const packing = useMemo(() => {
    return item.EMBALAGEM.find((e: PackagingProductProps) => {
      if (e?.QTD_MULTIPLO_VENDA > 1 && packType === 'PK') {
        return e.SG_EMBALAGEM === packType && e.QTD_MULTIPLO_VENDA?.toString() === qtd;
      }
      return e.SG_EMBALAGEM === packType && e.QTD_EMBALAGEM?.toString() === qtd;
    });
  }, [item, packType, qtd]) as PackagingProductProps;

  const { currentCart } = useAddToCart({
    sku: item?.COD_PRODUTO,
    packingOption: `${packing?.SG_EMBALAGEM}_${packing?.QTD_MULTIPLO_VENDA > 1 ? packing?.QTD_MULTIPLO_VENDA : packing?.QTD_EMBALAGEM}`,
  });

  const currentQuantity = useMemo(() => {
    const byUid = currentCartDetails?.items?.find((i) => i.uid === item.uid);
    if (byUid) return byUid.quantity;

    return (
      currentCart?.find((i) => {
        const [prodPack, qty] = i?.option_uid?.split('_') || [];
        if (packing?.QTD_MULTIPLO_VENDA > 1) {
          return (
            i?.sku === item?.COD_PRODUTO && packing?.SG_EMBALAGEM === prodPack && qty === packing?.QTD_MULTIPLO_VENDA?.toString()
          );
        }
        return i?.sku === item?.COD_PRODUTO && prodPack === packing?.SG_EMBALAGEM && qty === packing?.QTD_EMBALAGEM?.toString();
      })?.quantity || 0
    );
  }, [
    currentCartDetails?.items,
    item.uid,
    currentCart,
    item?.COD_PRODUTO,
    packing?.QTD_EMBALAGEM,
    packing?.QTD_MULTIPLO_VENDA,
    packing?.SG_EMBALAGEM,
  ]);

  const hasMultiplePacking = useMemo(
    () =>
      item?.EMBALAGEM?.length > 1 && currentCart?.find((i: CartItems) => i?.sku === item?.COD_PRODUTO)
        ? currentCart?.find((i: CartItems) => i?.sku === item?.COD_PRODUTO)
        : null,
    [item, currentCart]
  );

  // Qtd total unificada do produto adicionadas ao carrinho, quando o produto possui múltiplas embalagens
  const totalQuantity = useMemo(() => {
    if (hasMultiplePacking) {
      let count = 0;
      currentCart?.reduce((acc, currentValue) => {
        if (currentValue?.sku === item?.COD_PRODUTO) {
          const [, qtd] = currentValue?.option_uid?.split('_') || [];

          const qty = Number(qtd);
          const val = currentValue?.quantity * qty;
          count = acc += val;
        }
        return acc;
      }, 0);
      return count;
    }
    return 0;
  }, [currentCart, hasMultiplePacking, item?.COD_PRODUTO]);

  // Qtd total adicionada ao carrinho, calculada com base em produtos com ou sem múltiplas embalagens
  const rawCurrentAddedQty = useMemo(() => {
    const maximum = packing?.QTD_MULTIPLO_VENDA > 1;

    if (hasMultiplePacking && hasMultiplePacking?.sku) {
      return totalQuantity;
    } else if (!hasMultiplePacking?.sku && currentQuantity) {
      return maximum ? packing?.QTD_MULTIPLO_VENDA * currentQuantity : packing?.QTD_EMBALAGEM * currentQuantity;
    }
    return 0;
  }, [currentQuantity, hasMultiplePacking, packing?.QTD_EMBALAGEM, packing?.QTD_MULTIPLO_VENDA, totalQuantity]);

  // Qtd de produto em estoque calculada com base na quantidade adicionada ao carrinho
  const rawQuantity = useMemo(() => {
    const limit = getLimit(item?.COD_PRODUTO);
    const stockRemaining = stock?.[item?.COD_PRODUTO]?.stock_left;

    // Valida se existe controle de estoque local - para o caso deste produto já ter sido comprado antes

    if (stock?.[item?.COD_PRODUTO]) {
      if (stockRemaining && stockRemaining > 0) {
        if (rawCurrentAddedQty) {
          return stockRemaining <= rawCurrentAddedQty ? 0 : stockRemaining - rawCurrentAddedQty;
        }
        return stockRemaining;
      }
      return 0;
    }

    // Se não tiver controle de estoque local para este produto, vamos tomar como base o estoque que veio do dataset

    if (rawCurrentAddedQty) {
      return limit <= rawCurrentAddedQty ? 0 : limit - rawCurrentAddedQty;
    }
    return limit;
  }, [getLimit, item?.COD_PRODUTO, stock, rawCurrentAddedQty]);

  // Controle de estoque geral
  const hasStock = useMemo(() => {
    const limit = getLimit(item?.COD_PRODUTO);
    const multiple = packing?.QTD_MULTIPLO_VENDA > 1;
    const stockRemaining = stock?.[item?.COD_PRODUTO]?.stock_left;

    if (stock?.[item?.COD_PRODUTO]) {
      if (stockRemaining && stockRemaining > 0) {
        if (rawCurrentAddedQty) {
          return stockRemaining > 0 && stockRemaining >= rawCurrentAddedQty;
        }
        return multiple
          ? stockRemaining > 0 && stockRemaining >= packing?.QTD_MULTIPLO_VENDA
          : stockRemaining > 0 && stockRemaining >= packing?.QTD_EMBALAGEM;
      }
      return null;
    }
    if (rawCurrentAddedQty) {
      return limit > 0 && limit >= rawCurrentAddedQty;
    }
    return multiple ? limit > 0 && limit >= packing?.QTD_MULTIPLO_VENDA : limit > 0 && limit >= packing?.QTD_EMBALAGEM;
  }, [getLimit, packing?.QTD_EMBALAGEM, packing?.QTD_MULTIPLO_VENDA, item?.COD_PRODUTO, rawCurrentAddedQty, stock]);

  const lastPurchase = useMemo(() => {
    if (!item?.ultimaCompra) return null;

    if (item?.ultimaCompra?.SEQPESSOA === selectedClient?.COD_CLIENTE) {
      return {
        date: new Date(item?.ultimaCompra?.DT_COMPRA).toLocaleDateString('pt-BR'),
        quantity: item?.ultimaCompra?.QTDATENDIDA,
        value: item?.ultimaCompra?.VALOR,
      };
    } else return null;
  }, [item]);

  // O card de combo é sempre renderizado a partir de um item AGREGADO (aggregateComboDisplayItems/
  // mergeCompleteComboCartSections), cujo `displayKey` foi reescrito (`combo-agg-*`/`combo-merged-*`)
  // e não bate mais com a chave que o restante do app (ex.: DiscountFlexAmountContext) reconstrói a
  // partir de `buildComboDisplaySplitsForCartLine` (`${uid}-${comboInstanceId}` / `${uid}-excess`).
  // Por isso reconstruímos a mesma chave aqui em vez de usar `item.displayKey` diretamente — senão o
  // flex salvo neste item nunca é encontrado ao somar o total consumido do carrinho.
  const discountScopeKey = item.comboInstanceId
    ? `${item.uid}-${item.comboInstanceId}`
    : item.comboPortion === 'excess'
      ? `${item.uid}-excess`
      : item.displayKey ?? item.uid;

  const discountPrice = useMemo(() => {
    let packQtd = packing?.QTD_EMBALAGEM;
    if (packing?.QTD_MULTIPLO_VENDA > 1) {
      packQtd = packing?.QTD_MULTIPLO_VENDA;
    }
    return getDiscountProduct({
      package: packType,
      productId: item.COD_PRODUTO,
      quantity: packQtd,
      uid: discountScopeKey,
    })?.discountPrice?.[modePayment];
  }, [packing, getDiscountProduct, packType, item.COD_PRODUTO, modePayment, discountScopeKey]);

  const amountProduct = useMemo(() => {
    const packOption = packing?.SG_EMBALAGEM;
    let packQtd = packing?.QTD_EMBALAGEM;

    if (packing?.QTD_MULTIPLO_VENDA > 1) {
      packQtd = packing?.QTD_MULTIPLO_VENDA;
    }
    return getDiscountProduct({
      package: packOption,
      quantity: packQtd,
      productId: item.COD_PRODUTO,
      uid: discountScopeKey,
    });
  }, [packing, item.COD_PRODUTO, getDiscountProduct, discountScopeKey]);

  const currentPrice = useMemo(() => {
    const fetchedPrice = fetchCorrectPrice(item?.PRECO, item?.option_uid, modePayment);
    if (fetchedPrice?.VLR_FINAL || fetchedPrice?.VLR_PRECO) return fetchedPrice;
    return item?.currentPrice || ({} as PriceProductProps);
  }, [fetchCorrectPrice, item?.PRECO, item?.option_uid, item?.currentPrice, modePayment]);

  const usesComboSectionCounter = isInComboGroup && item?.comboPortion !== 'excess';
  const usesPortionCounter = !usesComboSectionCounter && Boolean(item?.comboPortion);

  const comboLineAllocations = useMemo(
    () => getComboCartItemInfo(item.uid),
    [getComboCartItemInfo, item.uid, currentCartDetails?.items]
  );

  const sectionDisplayQuantity = useMemo(() => {
    if (!usesComboSectionCounter) {
      return item?.displayQuantity ?? currentQuantity;
    }

    return getLiveComboDisplayPortionQuantity(comboLineAllocations, {
      sku: item.COD_PRODUTO,
      option_uid: item.option_uid,
      cartQuantity: currentQuantity,
      comboPortion: 'combo',
      comboInstanceId: item?.comboInstanceId,
      mergedInstanceIds: item?.mergedInstanceIds,
    });
  }, [
    usesComboSectionCounter,
    comboLineAllocations,
    item.COD_PRODUTO,
    item.option_uid,
    item?.comboInstanceId,
    item?.mergedInstanceIds,
    item?.displayQuantity,
    currentQuantity,
  ]);

  const portionQuantity = usesPortionCounter ? (item?.displayQuantity ?? currentQuantity) : sectionDisplayQuantity;

  const siblingQuantity = usesPortionCounter ? (item?.siblingQuantity ?? 0) : 0;

  const applyComboSectionAdjustment = useCallback(
    (delta: number) => {
      const targetInstanceIds = resolveComboSectionTargetInstanceIds(item);
      const [, qtdEmb] = item.option_uid?.split('_') || [];
      const units = Math.abs(delta);

      if (!targetInstanceIds.length || units <= 0) return;

      const hint = {
        cartItemUid: item.uid,
        cod_produto: item.COD_PRODUTO,
        qtd_embalagem: Number(qtdEmb),
        targetInstanceIds,
        units,
      };

      if (delta < 0) {
        setPendingComboSectionDecrement(hint);
      } else {
        setPendingComboSectionIncrement(hint);
      }

      updateProductAmount({
        uid: item.uid,
        sku: item.COD_PRODUTO,
        quantity: Math.max(0, currentQuantity + delta),
      });
    },
    [item, currentQuantity, updateProductAmount]
  );

  const handleComboSectionDecrement = useCallback(
    () => applyComboSectionAdjustment(-1),
    [applyComboSectionAdjustment]
  );

  const handleComboSectionIncrement = useCallback(
    () => applyComboSectionAdjustment(1),
    [applyComboSectionAdjustment]
  );

  const validateFlexRemoval = useCallback(
    (unitsToRemove: number) => {
      if (!discountPrice || !rcaSaldoFlex) return true;

      const discountPercentual = amountProduct?.percentual || 0;
      const unitDiscount = discountPrice - getOriginalValue(discountPrice, discountPercentual);
      const productDiscount = unitDiscount * unitsToRemove;

      if (rcaSaldoFlex - productDiscount + discountAmount < 0) {
        setTooltip(true);
        setTimeout(() => setTooltip(false), 5000);
        return false;
      }

      return true;
    },
    [discountPrice, rcaSaldoFlex, amountProduct?.percentual, discountAmount]
  );

  const finalizeCartLineRemoval = useCallback(() => {
    const packOption = packing?.SG_EMBALAGEM;
    let packQtd = packing?.QTD_EMBALAGEM;

    if (packing?.QTD_MULTIPLO_VENDA > 1) {
      packQtd = packing?.QTD_MULTIPLO_VENDA;
    }

    deleteDiscount({
      package: packOption,
      quantity: packQtd,
      productId: item.COD_PRODUTO,
      uid: discountScopeKey,
    });
    removeProductFromCart({ uid: item.uid });
  }, [packing, item.COD_PRODUTO, item.uid, discountScopeKey, deleteDiscount, removeProductFromCart]);

  const handleComboSectionRemove = useCallback(() => {
    const unitsToRemove = sectionDisplayQuantity;
    if (unitsToRemove <= 0) return;

    if (!validateFlexRemoval(unitsToRemove)) return;

    const nextQuantity = Math.max(0, currentQuantity - unitsToRemove);
    applyComboSectionAdjustment(-unitsToRemove);

    if (nextQuantity <= 0) {
      finalizeCartLineRemoval();
    }
  }, [
    sectionDisplayQuantity,
    validateFlexRemoval,
    currentQuantity,
    applyComboSectionAdjustment,
    finalizeCartLineRemoval,
  ]);

  const handleRemoveOnCart = useCallback(() => {
    if (usesPortionCounter && siblingQuantity > 0) {
      updateProductAmount({
        uid: item.uid,
        sku: item.COD_PRODUTO,
        quantity: siblingQuantity,
      });
      return;
    }

    if (usesComboSectionCounter) {
      handleComboSectionRemove();
      return;
    }

    if (!validateFlexRemoval(portionQuantity)) return;

    finalizeCartLineRemoval();
  }, [
    portionQuantity,
    siblingQuantity,
    usesPortionCounter,
    usesComboSectionCounter,
    handleComboSectionRemove,
    validateFlexRemoval,
    item.uid,
    item.COD_PRODUTO,
    updateProductAmount,
    finalizeCartLineRemoval,
  ]);

  const comboSectionCounter = usesComboSectionCounter
    ? {
        displayQuantity: sectionDisplayQuantity,
        onDecrement: handleComboSectionDecrement,
        onIncrement: handleComboSectionIncrement,
        onAdjust: applyComboSectionAdjustment,
        onRemove: handleComboSectionRemove,
      }
    : undefined;

  const isLooseComboParticipant = Boolean(item?.showLooseComboParticipant);
  const isExcessPortion = item?.comboPortion === 'excess';
  const isComboRelatedItem = Boolean(item?.comboPortion || isLooseComboParticipant);

  const comboCartPrice = useComboCartPrice({
    cartItemUid: item.uid,
    product: item,
    option_uid: item.option_uid,
    paymentCondition: modePayment,
    enabled: isComboRelatedItem,
    applyComboDiscount: isComboRelatedItem && !isExcessPortion && (item?.comboDiscountQuantity ?? portionQuantity) > 0,
  });

  const hasComboDiscount = comboCartPrice.hasComboDiscount;

  // Preço de referência riscado: SEMPRE o preço-base antes do flex — de combo quando houver, normal
  // caso contrário. Nunca o valor do flex (senão o "riscado" vira o próprio preço com desconto).
  const originalValuePrice = useMemo(() => {
    if (hasComboDiscount && comboCartPrice.originalUnitPrice != null) {
      return comboCartPrice.originalUnitPrice;
    }
    return currentPrice?.VLR_FINAL || currentPrice?.VLR_PRECO;
  }, [hasComboDiscount, comboCartPrice.originalUnitPrice, currentPrice]);

  // Preço-base antes do flex (combo, se houver; normal caso contrário) — é sobre ele que o flex
  // aplicado no modal foi calculado.
  const baseBeforeFlexPrice = useMemo(() => {
    if (hasComboDiscount && comboCartPrice.comboUnitPrice != null) {
      return comboCartPrice.comboUnitPrice;
    }
    return currentPrice?.VLR_FINAL || currentPrice?.VLR_PRECO;
  }, [hasComboDiscount, comboCartPrice.comboUnitPrice, currentPrice]);

  // Preço final exibido: o flex aplicado (já calculado em cima da base correta) tem prioridade sobre
  // qualquer recálculo de combo — senão o card ignora o flex e mostra só o preço de combo puro.
  const valuePrice = useMemo(() => {
    if (discountPrice) return discountPrice;
    return baseBeforeFlexPrice;
  }, [discountPrice, baseBeforeFlexPrice]);

  const flexModalPrice = useMemo(
    () =>
      (hasComboDiscount && comboCartPrice.comboPriceRecord
        ? comboCartPrice.comboPriceRecord
        : currentPrice) as PriceProductProps,
    [hasComboDiscount, comboCartPrice.comboPriceRecord, currentPrice]
  );

  // Mesma regra de packQtd usada em discountPrice/amountProduct — calculada uma única vez aqui para
  // que flexGroupEntries e o efeito de sincronização logo abaixo nunca divirjam entre si.
  const flexSyncPackQtd = useMemo(() => {
    return packing?.QTD_MULTIPLO_VENDA > 1 ? packing?.QTD_MULTIPLO_VENDA : packing?.QTD_EMBALAGEM;
  }, [packing]);

  // Grupo de porções (kit completo, kit em andamento, excedente) do MESMO produto+embalagem no
  // carrinho — mesmo prefixo de chave usado por getDiscountProduct/setDiscount, ignorando o sufixo
  // de porção (uid/comboInstanceId/excess).
  const flexGroupEntries = useMemo(() => {
    if (!packType || !flexSyncPackQtd) return [];

    const prefix = `${item.COD_PRODUTO}-${packType}-${flexSyncPackQtd}-`;
    const clientDiscounts = discounts?.[selectedClient?.COD_CLIENTE] || {};
    return Object.entries(clientDiscounts).filter(([key]) => key.startsWith(prefix));
  }, [discounts, selectedClient?.COD_CLIENTE, item.COD_PRODUTO, packType, flexSyncPackQtd]);

  // Propaga uma única % de flex entre as porções do mesmo produto+embalagem: quando outra porção
  // (combo completo/em andamento/excedente) é a mudança mais recente (updatedAt), esta porção
  // recalcula seu próprio preço final naquela % a partir da sua própria base (flexModalPrice) —
  // preço de combo se for kit completo, preço normal se for excedente/kit incompleto.
  useEffect(() => {
    // >= 1, não >= 2: na primeira propagação a porção irmã ainda não tem entrada própria salva —
    // o grupo só tem a entrada de QUEM aplicou o flex, e é exatamente esse caso que precisa sincronizar.
    if (flexGroupEntries.length < 1) return;

    let authoritativeKey: string | null = null;
    let authoritativeEntry: (typeof flexGroupEntries)[number][1] | null = null;
    for (const [key, entry] of flexGroupEntries) {
      const isNewer =
        (entry?.updatedAt || 0) > (authoritativeEntry?.updatedAt || 0) ||
        // Desempate: mesmo updatedAt (mesmo milissegundo) — usa o contador monotônico da gravação.
        ((entry?.updatedAt || 0) === (authoritativeEntry?.updatedAt || 0) &&
          (entry?.updatedAtSeq || 0) > (authoritativeEntry?.updatedAtSeq || 0));
      if (!authoritativeEntry || isNewer) {
        authoritativeKey = key;
        authoritativeEntry = entry;
      }
    }
    if (!authoritativeEntry) return;

    const myKey = `${item.COD_PRODUTO}-${packType}-${flexSyncPackQtd}-${discountScopeKey}`;
    if (authoritativeKey === myKey) return;

    const targetPercent = authoritativeEntry?.percentual || 0;
    const myOwnPercent = amountProduct?.percentual || 0;
    if (targetPercent === myOwnPercent) return;

    const result = targetPercent
      ? computeSyncedDiscount(flexModalPrice, targetPercent, selectedClientScope, substTrib)
      : { price: 0, saldoAFaturar: 0 };

    // flexModalPrice ainda não carregou (preço-base inválido) — não grava nada agora; o efeito roda
    // de novo assim que a base ficar válida, já que myOwnPercent continua diferente de targetPercent.
    if (targetPercent && result.price <= 0) return;

    setDiscount({
      package: packType,
      quantity: flexSyncPackQtd,
      productId: item.COD_PRODUTO,
      uid: discountScopeKey,
      percentual: targetPercent,
      // Mescla com o discountPrice já salvo desta porção — nunca sobrescrever preços de outras
      // condições de pagamento que não a atual (modePayment).
      price: targetPercent ? { ...(amountProduct?.discountPrice || {}), [modePayment]: result.price } : {},
      saldoAFaturar: result.saldoAFaturar,
    });
  }, [
    flexGroupEntries,
    item.COD_PRODUTO,
    packType,
    flexSyncPackQtd,
    discountScopeKey,
    amountProduct?.percentual,
    amountProduct?.discountPrice,
    flexModalPrice,
    selectedClientScope,
    substTrib,
    modePayment,
    setDiscount,
  ]);

  const priceForDisplay = useMemo(() => formatCurrency({ value: valuePrice }), [valuePrice]);

  const originalPriceForDisplay = useMemo(
    () => formatCurrency({ value: originalValuePrice }),
    [originalValuePrice]
  );

  const unityPrice = useMemo(() => {
    const qtd = packing?.QTD_MULTIPLO_VENDA > 1 ? packing?.QTD_MULTIPLO_VENDA : packing?.QTD_EMBALAGEM;

    return formatCurrency({
      value: valuePrice / qtd,
    });
  }, [packing, valuePrice]);

  const originalUnityPrice = useMemo(() => {
    const qtd = packing?.QTD_MULTIPLO_VENDA > 1 ? packing?.QTD_MULTIPLO_VENDA : packing?.QTD_EMBALAGEM;

    return formatCurrency({
      value: originalValuePrice / qtd,
    });
  }, [packing, originalValuePrice]);

  const rowPrice = useMemo(
    () =>
      formatCurrency({
        value: valuePrice * portionQuantity,
      }),
    [valuePrice, portionQuantity]
  );

  const originalRowPrice = useMemo(
    () =>
      formatCurrency({
        value: originalValuePrice * portionQuantity,
      }),
    [originalValuePrice, portionQuantity]
  );

  const priceRecordForSt = hasComboDiscount ? comboCartPrice.comboPriceRecord : currentPrice;

  const limitFlex = useMemo(() => {
    const percentual = Number(amountProduct?.percentual || 0);
    // Para acréscimos (percentual >= 0), não limitar via flex no carrinho
    if (percentual >= 0) return Infinity;

    const discountPrice = Number(amountProduct?.discountPrice?.[`${paymentCode}`] || 0);
    if (!discountPrice) return Infinity; // sem preço flex aplicado, não limitar

    const originalValue = getOriginalValue(discountPrice, percentual) || 0;
    const diffPrice = originalValue - discountPrice; // deve ser > 0 para desconto
    if (diffPrice <= 0) return Infinity;

    const saldoDisponivel = (rcaSaldoFlex || 0) + discountAmount; // consumo efetivo (só descontos)
    const adicionaisPossiveis = Math.trunc(saldoDisponivel / diffPrice);

    // Se não houver saldo disponível, não permitir aumentar além do que já tem
    if (adicionaisPossiveis <= 0) return currentQuantity;

    return adicionaisPossiveis + currentQuantity;
  }, [paymentCode, rcaSaldoFlex, discountAmount, currentQuantity, amountProduct?.percentual, amountProduct?.discountPrice]);

  const tooltipMessage = useMemo(() => {
    return `A remoção deste item fará com que o saldo flex fique negativo: ${formatCurrency({
      value: (rcaSaldoFlex || 0) + discountAmount,
    })}`;
  }, [rcaSaldoFlex, discountAmount]);

  const packingUnities = useMemo(() => {
    const qtdEmb = packing?.QTD_MULTIPLO_VENDA > 1 ? packing?.QTD_MULTIPLO_VENDA : packing?.QTD_EMBALAGEM;

    return qtdEmb > 1 ? `${qtdEmb} unidades` : `${qtdEmb} unidade`;
  }, [packing]);

  const qtdPerPack = useMemo(() => {
    return packing?.QTD_MULTIPLO_VENDA > 1 ? packing?.QTD_MULTIPLO_VENDA : packing?.QTD_EMBALAGEM;
  }, [packing]);

  const packingLabel = useMemo(() => {
    const emb = packing?.SG_EMBALAGEM;
    return emb;
  }, [packing]);

  const showInfosFlex = useMemo(() => {
    if (!item.EMBALAGEM) return false;

    const packQuantity = Number(qtd);
    const clientLoja = Number(selectedClientScope?.COD_LOJA);
    const clientSegs = (selectedClientScope?.COD_SEGMENTO || [])
      .map((s: any) => Number(s?.cod_segmento ?? s?.COD_SEGMENTO ?? s))
      .filter((n: number) => Number.isFinite(n));

    const embalagem = item.EMBALAGEM.find((p: PackagingProductProps) => {
      const matchesPack =
        packType === 'PK'
          ? p.SG_EMBALAGEM === packType && packQuantity === p.QTD_MULTIPLO_VENDA
          : p.SG_EMBALAGEM === packType && packQuantity === p.QTD_EMBALAGEM;
      if (!matchesPack) return false;
      if (clientLoja > 0 && Number(p.COD_LOJA) !== clientLoja) return false;
      if (clientSegs.length && !clientSegs.includes(Number(p.COD_SEGMENTO))) return false;
      return true;
    });

    if (!embalagem) return false;
    return embalagem.PERC_MAX_FLEX !== 0 && embalagem.PERC_MIN_FLEX !== 0;
  }, [packType, qtd, item.EMBALAGEM, selectedClientScope?.COD_LOJA, selectedClientScope?.COD_SEGMENTO]);

  const handleOpenAttributesModal = () => {
    openModal({
      product: item,
      packQuantity: qtdPerPack,
      price: currentPrice as PriceProductProps,
      pack: PackingTypes?.[packingLabel]?.shortName,
    })
  };

  const openFlex = () => {
    openModalFlex({
      product: item,
      pack: PackingTypes?.[packingLabel]?.shortName,
      packQuantity: qtdPerPack,
      price: flexModalPrice,
      substTrib: substTrib || [],
      hasStock: hasStock || Math.floor(rawQuantity / qtdPerPack) > 0,
      rawQuantity,
      uid: item.uid,
      discountScopeKey,
      fromCart: true,
    });
  };

  if (portionQuantity === 0) {
    return null;
  }

  return (
    <S.CardWrapper $isInComboGroup={isInComboGroup}>
      <S.OuterGrid>
      <S.Container $alignitems="center">
        <S.ContentImage to={`/${item.COD_PRODUTO}`}>
          <S.ProductImage>
            <ProductImage COD_PRODUTO={item?.COD_PRODUTO} />
          </S.ProductImage>
        </S.ContentImage>
        <ToolTip direction="top" show={tooltip} displayMode="default" text={tooltipMessage}>
          <S.ButtonContainer data-test={`remove-product-button-${item?.COD_PRODUTO}`}>
            <S.RemoveButton onClick={handleRemoveOnCart}>
              <S.RemoveButtonLabel>Remover</S.RemoveButtonLabel>
            </S.RemoveButton>
          </S.ButtonContainer>
        </ToolTip>

        <S.Selo>
          <SeloCampanha codProduto={item?.COD_PRODUTO} />
        </S.Selo>
      </S.Container>

      <S.Container>
        <S.InnerBlock $extraPadding={true}>
          <S.DescriptionBlock>
            <S.DescriptionRow>
              <S.ProductDescription to={`/${item.COD_PRODUTO}`}>
                {cardDescription === CardDescription.DC ? item?.DESC_PRODUTO_BUSCA || item?.DESC_PRODUTO : item?.DESC_PRODUTO || item?.DESC_PRODUTO_BUSCA}
              </S.ProductDescription>
              {showInfosFlex && (
                <S.FlexPercentage
                  onClick={openFlex}
                  $positive={(amountProduct?.percentual ?? 0) > 0}
                  $negative={(amountProduct?.percentual ?? 0) < 0}
                >
                  <RiExchangeDollarLine size={20} color={colors.brand.grafite} />
                  {amountProduct?.percentual ? (
                    <S.FlexPercentageLabel>{amountProduct.percentual.toFixed(2)}%</S.FlexPercentageLabel>
                  ) : (
                    <S.FlexPercentageLabel>Flex</S.FlexPercentageLabel>
                  )}
                </S.FlexPercentage>
              )}
            </S.DescriptionRow>
            {hasStock && (
              <S.StockContainer>
                <S.IconContainer onClick={handleOpenAttributesModal}>
                  <RiInformation2Line size={24} color={colors.gray.gray40} />
                </S.IconContainer>
                {hasStock && <Stock stockLeft={Math.floor(rawQuantity / qtdPerPack)} height={36} extraMarginTop={false} />}
              </S.StockContainer>
            )}
          </S.DescriptionBlock>
          {valuePrice ? (
            <S.PriceBlock>
              {hasComboDiscount ? (
                <>
                  <S.PriceRow $old>{originalRowPrice}</S.PriceRow>
                  <S.PriceRow $combo>{rowPrice}</S.PriceRow>
                </>
              ) : (
                <S.PriceRow>{rowPrice}</S.PriceRow>
              )}
              <StPrice
                precoSt={priceRecordForSt?.ST_VLR_FINAL}
                precoCalculado={priceRecordForSt?.VLR_PRECO_CALCULADO}
                precoPauta={priceRecordForSt?.VLR_PAUTA}
                codTributacaoProduto={priceRecordForSt?.COD_TRIBUTACAO_PRODUTO}
                quantidade={portionQuantity}
                cliente={selectedClient}
                descontoFlex={amountProduct?.percentual ?? 0}
              />
              {hasComboDiscount ? (
                <>
                  <S.Price $old>{originalPriceForDisplay}</S.Price>
                  <S.Price $combo>{priceForDisplay}</S.Price>
                  <S.UnityPrice $old>{originalUnityPrice}/un.</S.UnityPrice>
                  <S.UnityPrice $combo>{unityPrice}/un.</S.UnityPrice>
                </>
              ) : (
                <>
                  <S.Price>{priceForDisplay}</S.Price>
                  <S.UnityPrice>{unityPrice}/un.</S.UnityPrice>
                </>
              )}
            </S.PriceBlock>
          ) : null}
        </S.InnerBlock>
        <S.InnerBlock>
          <S.PackingBlock>
            <S.IconContainer $radius={4}>
              <S.Packing src={PackingTypes?.[packingLabel]?.icon} />
            </S.IconContainer>
            <S.PackingDescription>
              <S.PackingLabel>{PackingTypes?.[packingLabel]?.packingTitle}</S.PackingLabel>
              <S.PackingUnities>{packingUnities}</S.PackingUnities>
            </S.PackingDescription>
          </S.PackingBlock>
          <S.ButtonContainer>
            <S.CounterButton />
            {!valuePrice ? (
              <S.Unavailable>Indisponível</S.Unavailable>
            ) : (
              <Counter
                showBorder
                pack={packing}
                product={item}
                background="white"
                limitFlex={limitFlex}
                id={item?.displayKey || item?.allocationId || `${item.COD_PRODUTO}-${item.uid}`}
                discountProduct={amountProduct}
                cartItemUid={usesComboSectionCounter || usesPortionCounter ? item.uid : undefined}
                comboSectionCounter={comboSectionCounter}
                portionQuantity={usesPortionCounter ? portionQuantity : undefined}
                siblingQuantity={usesPortionCounter ? siblingQuantity : undefined}
              />
            )}
          </S.ButtonContainer>
        </S.InnerBlock>
        {lastPurchase && (
          <S.LastPurchaseContainer>
            <S.LastPurchaseBox>
              <S.LastPurchaseLabel>Última venda:</S.LastPurchaseLabel>
              <S.LastPurchaseValue>{lastPurchase?.date}</S.LastPurchaseValue>
            </S.LastPurchaseBox>
            <S.LastPurchaseBox>
              <S.LastPurchaseLabel>Qtd:</S.LastPurchaseLabel>
              <S.LastPurchaseValue>
                {lastPurchase?.quantity} {lastPurchase?.quantity >= 1 ? 'unidades' : 'unidade'}
              </S.LastPurchaseValue>
            </S.LastPurchaseBox>
            <S.LastPurchaseBox>
              <S.LastPurchaseValue>{formatCurrency({ value: lastPurchase?.value })}</S.LastPurchaseValue>
            </S.LastPurchaseBox>
          </S.LastPurchaseContainer>
        )}
      </S.Container>
      </S.OuterGrid>

      {(isInComboGroup || isLooseComboParticipant) && (
        <SeloCombo
          cartItemUid={item?.uid}
          cartItemUids={item?.comboSourceUids}
          cartItemSku={item.sku}
          cartItemOptionUid={item.option_uid}
          idCampanha={item?.idCampanha}
          comboInstanceId={item?.comboInstanceId}
          comboPortion={item?.comboPortion}
          displayQuantity={item?.displayQuantity}
          comboDiscountQuantity={item?.comboDiscountQuantity}
          variant="strip"
        />
      )}
    </S.CardWrapper>
  );
};

export default CartProductCard;
